import { Breadcrumb } from '@/components/atoms';
import { TableOfContents, SiloSidebar } from '@/components/molecules';
import { BlockRenderer } from '@/components/BlockRenderer';
import { decodeHtmlEntities } from '@/utils/decodeHtmlEntities';
import { sanitizeLinkHref } from '@/utils/sanitizeLinkHref';
import arrowYellow from '@/assets/icons/icon-arrow-right-yellow.png';
import type { ReactElement } from 'react';
import type { AppData } from '@/types/app';
import './Page.scss';

interface PageProps {
  data: AppData;
}

export function Page({ data }: PageProps) {
  const { post, blocks, silo } = data;

  const breadcrumbItems = post?.breadcrumbs ?? [
    { name: post?.title ?? '' },
  ];

  const hasDescription = !!post?.description;
  const tocItems = post?.toc ?? [];
  const hasToc = tocItems.length > 0;

  let body: ReactElement;

  if (hasDescription || hasToc) {
    const descriptive = (
      <div className="page-descriptive">
        <Breadcrumb items={breadcrumbItems} />

        {hasDescription ? (
          <header className="page-descriptive__banner">
            <div className="page-descriptive__banner-left">
              <h1 className="page-descriptive__banner-title">
                {decodeHtmlEntities(post?.title ?? '')}
              </h1>
              {post?.subtitle && (() => {
                const safeHref = sanitizeLinkHref(post.subtitle_url);
                return (
                  <p className="page-descriptive__banner-subtitle">
                    <img src={arrowYellow} alt="" aria-hidden="true" width="16" height="12" />
                    {safeHref ? (
                      <a href={safeHref}>{decodeHtmlEntities(post.subtitle)}</a>
                    ) : (
                      decodeHtmlEntities(post.subtitle)
                    )}
                  </p>
                );
              })()}
            </div>
            <div
              className="page-descriptive__banner-description"
              dangerouslySetInnerHTML={{ __html: post.description ?? '' }}
            />
          </header>
        ) : (
          <h1 className="page-descriptive__title">
            {decodeHtmlEntities(post?.title ?? '')}
          </h1>
        )}

        {hasToc && (
          <TableOfContents items={tocItems} />
        )}

        <div className="page-descriptive__content">
          <BlockRenderer blocks={blocks} />
        </div>
      </div>
    );

    body = silo ? (
      <div className="with-silo-sidebar">
        {descriptive}
        <SiloSidebar silo={silo} />
      </div>
    ) : descriptive;
  } else {
    const defaultLayout = (
      <div className="page-default">
        <header className="page-default__header">
          <Breadcrumb items={breadcrumbItems} />
          <h1 className="page-default__title">
            {decodeHtmlEntities(post?.title ?? '')}
          </h1>
        </header>

        <div className="page-default__content">
          <BlockRenderer blocks={blocks} />
        </div>
      </div>
    );

    body = silo ? (
      <div className="with-silo-sidebar">
        {defaultLayout}
        <SiloSidebar silo={silo} />
      </div>
    ) : defaultLayout;
  }

  return body;
}
